<script>
import i18n from "@/plugins/i18n";
import { mapActions } from "vuex";

const SustLogo = "/images/SustAppLogos/sustainfinity_256x256.png";
const CarbonLogo = "/images/SustAppLogos/Sustainfinity-Carbon-Logo.png";
const KpiBG = "/images/SustAppLogos/kpi.png";
const SupplierBG = "/images/SustAppLogos/supplier.png";
const AdminBG = "/images/SustAppLogos/admin.png";

export default {
    data() {
        return {
            apps: [
                { val: "sust", type: "app", logo: SustLogo, text: "Sustainfinity", icon: null},
                { val: "carbon", type: "app", logo: CarbonLogo, text: "Carbon Module", icon: null}
            ],
            modules: [
                { val: "kpi", type: "module", logo: KpiBG, position: 'top', text: i18n.t('dropdown.kpi'), icon: "fa-solid fa-chart-line"},
                { val: "supplier", type: "module", logo: SupplierBG, position: 'center', text: i18n.t('dropdown.supplier'), icon: "fi fi-rr-supplier-alt"},
                { val: "admin", type: "module", logo: AdminBG, position: 'top', text: i18n.t('dropdown.admin'), icon: "fi fi-rr-user-gear"}
            ],

            hoveredModule: "",

            licenceInfo: null,
            directors: [],
            officers: [],
            companyName: "Company Name",
        }
    },

    mounted() {
        if (window.opener) {
          window.opener.postMessage("is_logged_in", process.env.VUE_APP_CARBON_DOMAIN);
        }

        this._getCompanyLicence();
        this._getAppSelectionInfo();
    },

    methods: {
        ...mapActions('company', [
            'getCompanyLicence',
            'getAppSelectionInfo',
        ]),

        _getCompanyLicence() {
            const params = {
                company_id: Number(this.$user.company_id)
            }
            this.getCompanyLicence(params)
                .then( res => {
                    this.licenceInfo = res.data[0];
                })
                .catch(({ response }) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "error",
                        duration: 5000,
                        dismissible: true,
                    });
                })
        },

        _getAppSelectionInfo() {
            this.getAppSelectionInfo({company_id: this.$user.company_id})
                .then( res => {
                    this.companyName = res.data.company_name;
                    this.directors = res.data.directors;
                    this.officers = res.data.officers;
                })
                .catch(({ response }) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "error",
                        duration: 5000,
                        dismissible: true,
                    });
                })
        },

        clickItem(item) {
            if (item.val == "sust") {
                this.$router.push("/dashboard");
            } else if (item.val == "kpi") {
                this.$router.push("/kpi-metrics");
            } else if (item.val == "supplier") {
                this.$router.push("/supplier-metrics");
            } else if (item.val == "admin") {
                this.$router.push("/admin-panel?p=profile");
            } else if (item.val == "carbon") {
                window.open(process.env.VUE_APP_CARBON_DOMAIN);
            } else {
                throw new Error("Unexpected item clicked!");
            }
        },

        setHoveredModule(module) {
            this.hoveredModule = module;
        }
    }
}
</script>

<template>
    <div class="app-selection-page">
        <div class="top-section">
            <div class="apps">
                <div class="app" v-for="(item, index) in apps" :key="index">
                    <div class="image-container" @click="clickItem(item)">
                        <img :src="item.logo" :alt="item.text">
                    </div>
                </div>
            </div>

            <div class="modules">
                <div class="module" v-for="(item, index) in modules" :key="index" @click="clickItem(item)" @mouseover="setHoveredModule(item.val)" @mouseleave="setHoveredModule('')">
                    <div class="image-container">
                        <div class="color-overlay" :class="{'inactive-color-overlay': hoveredModule == item.val}"></div>
                        <img class="module-image" :src="item.logo" :alt="item.text" :style="{objectPosition: `center ${item.position}`}">
                    </div>
                    <div class="go-button">
                        {{ item.text }} <i class="fa-solid fa-arrow-right-long"></i>
                    </div>
                </div>
            </div>
        </div>

        <div class="bottom-section">
            <div class="header">
                {{ $t('appSelection.licence_info') }}
            </div>

            <div class="licence-info">
                <div class="company-name">
                    {{ companyName }}
                </div>

                <div class="bottom-info">
                    <div class="licence-dates">
                        <div class="licence-date">
                            <b>{{ $t('appSelection.sustainfinity') }}:</b>&emsp13;{{ licenceInfo.licence_start_date | dateFormat }} - {{ licenceInfo.licence_end_date | dateFormat }}
                            &emsp;
                            <b>{{ $t('appSelection.current_plan') }}:</b>&emsp13;{{ licenceInfo.plan_name }}
                        </div>
                    </div>

                    <div class="users">
                        <span v-for="(item, index) in directors" :key="index"><b>{{ $t('appSelection.director') }}:</b> {{ item.firstname }} {{ item.surname }}</span>
                        <span v-for="(item, index) in officers" :key="index"><b>{{ $t('appSelection.officer') }}:</b> {{ item.firstname }} {{ item.surname }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang='scss' scoped>
    .app-selection-page {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-content: center;
        align-items: center;
        width: 100%;
        height: 100vh;
        overflow-y: auto;

        > .top-section {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-content: center;
            align-items: center;
            flex-wrap: wrap;
            width: 100%;
            height: 50%;
            padding: 2em;
            background-color: #d4dddf;

            > .apps {
                flex: 1;
                display: flex;
                flex-direction: row;
                width: 100%;
                height: 60%;
                flex: .6;
                justify-content: center;
                align-content: center;
                align-items: stretch;

                > .app {
                    width: 40%;
                    height: 100%;
                    
                    > .image-container {
                        width: 75%;
                        height: 75%;
                        top: 50%;
                        left: 50%;

                        background-color: whitesmoke;
                        border-radius: 1em;
                        filter: drop-shadow(0 .3em .5em gray);

                        position: relative;
                        > img {
                            position: absolute;
                            transform: translate(-50%, -50%);
                            top: 50%;
                            left: 50%;
                            max-width: 256px;
                        }

                        transition: all 100ms;
                        cursor: pointer;
                        &:hover {
                            filter: drop-shadow(0 .3em .5em var(--sustainfinity-blue));
                            scale: 1.025;
                        }
                    }

                    &:first-of-type {
                        > .image-container {
                            transform: translate(-37.5%, -50%);
                        }
                    }
                    &:nth-of-type(2) {
                        > .image-container {
                            transform: translate(-62.5%, -50%);
                        }
                    }
                }
            }

            > .modules {
                flex: 1;
                display: flex;
                flex-direction: row;
                width: 100%;
                height: 40%;
                flex: .4;
                justify-content: center;
                align-content: center;
                align-items: stretch;

                > .module {
                    width: 23.5%;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-content: center;
                    align-items: center;
                    border-radius: 1em;
                    overflow: hidden;
                    margin: .5em;
                    filter: drop-shadow(0 .3em .5em gray);

                    transition: all 100ms;
                    cursor: pointer;
                    &:hover {
                        filter: drop-shadow(0 .3em .5em var(--sustainfinity-blue));
                        scale: 1.025;
                    }

                    > .image-container {
                        height: 80%;
                        width: 100%;

                        position: relative;

                        > img {
                            width: 100%;
                            height: 100%;
                            object-fit: cover;
                        }

                        > .color-overlay {
                            position: absolute;
                            height: 100%;
                            width: 100%;
                            background-color: whitesmoke;
                            opacity: .5;
                        }

                        > .inactive-color-overlay {
                            opacity: 0 !important;
                        }
                    }
                    > .go-button {
                        height: 100%;

                        font-size: 1.25em;
                        font-weight: bold;
                        font-style: italic;
                        display: flex;
                        flex-direction: row;
                        justify-content: space-between;
                        align-content: center;
                        align-items: center;
                        background-color: whitesmoke;
                        width: 100%;
                        padding: 1em 1em;
                    }
                }
            }
            
        }

        > .bottom-section {
            height: 50%;
            width: 100%;

            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-content: flex-start;

            padding-top: 1em !important;
            padding: 7em;

            color: black;

            > .header {
                width: 100%;
                text-align: left;
                font-weight: bold;
                font-size: 2em;
                padding-left: 1em;
                padding-bottom: .5em;
                border-bottom: .1em solid darkgray;
            }

            > .licence-info {
                width: 100%;
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-content: flex-start;

                > .company-name {
                    width: 100%;
                    font-size: 1.5em;
                    font-weight: bold;
                    padding-top: 1em;
                }
                
                > .bottom-info {
                    width: 100%;
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    align-content: flex-start;
                    align-items: flex-start;

                    padding-top: 2.5em;
                    font-size: 1.25em;

                    > .licence-dates {
                        display: flex;
                        flex-direction: column;
                        justify-content: flex-start;
                        align-content: flex-start;

                        > .licence-date {
                            display: flex;
                            flex-direction: row;
                            justify-content: flex-start;
                            align-content: center;
                        }
                    }

                    > .users {
                        display: flex;
                        flex-direction: column;
                        justify-content: flex-start;
                        align-content: flex-start;

                        margin-right: 5em;

                        > span {
                            padding-bottom: .5em;
                        }
                    }
                }
            }
        }
    }
</style>